// src/portals/creator/components/CreatorLayout.js
import React, { useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import CreatorSidebar from "./CreatorSidebar";
import MobileHeader from "./MobileHeader";
import MobileNavDrawer from "./MobileNavDrawer";
import CreatorNavLinks from "./CreatorNavLinks";
import { PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { useLayout } from "../../../context/LayoutContext";
import { useAuth } from "../../../context/AuthContext";

const CreatorLayout = () => {
  const [isMobileNavOpen, setMobileNavOpen] = useState(false);
  const [isSidebarCollapsed, setSidebarCollapsed] = useState(false);
  const { layoutMode } = useLayout();
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  // Determine if we should show the desktop sidebar
  // It should be hidden if we are in mobile layout mode
  const showDesktopSidebar = layoutMode !== "mobile";

  return (
    // ✨ This flex container is the base for the whole layout
    <div className="relative flex h-screen w-full overflow-hidden bg-slate-50">
      {/* --- DESKTOP SIDEBAR --- */}
      {showDesktopSidebar && (
        <div
          className={`hidden sm:flex sm:flex-col transition-all duration-300 bg-white border-r border-slate-200 h-full ${
            isSidebarCollapsed ? "w-20" : "w-64"
          }`}
        >
          <CreatorSidebar isCollapsed={isSidebarCollapsed} />
        </div>
      )}

      {/* --- MAIN CONTENT AREA --- */}
      <div className="flex-1 flex flex-col min-w-0">
        {" "}
        {/* min-w-0 is crucial to prevent squishing */}
        {/* Mobile-only Header - Show if in mobile mode OR on small screens */}
        <div className={showDesktopSidebar ? "sm:hidden" : ""}>
          <MobileHeader onMenuClick={() => setMobileNavOpen(true)} />
        </div>
        {/* Desktop-only Header with Collapse Button */}
        {showDesktopSidebar && (
          <div className="hidden sm:flex items-center p-4 bg-white border-b border-slate-200 h-[73px]">
            <button
              onClick={() => setSidebarCollapsed(!isSidebarCollapsed)}
              className="p-2 rounded-full hover:bg-slate-100"
            >
              {isSidebarCollapsed ? <PanelLeftOpen /> : <PanelLeftClose />}
            </button>
          </div>
        )}
        {/* ✨ THE SCROLL FIX: This main element now scrolls independently */}
        <main className="flex-1 p-4 sm:p-6 overflow-y-auto @container pb-24 sm:pb-6">
          <Outlet />
        </main>
      </div>

      {/* --- MOBILE DRAWER (hidden by default) --- */}
      {/* Mobile Navigation Drawer */}
      <MobileNavDrawer
        isOpen={isMobileNavOpen}
        onClose={() => setMobileNavOpen(false)}
        footerContent={
          <div className="p-4 space-y-4">
            {/* Back to Fan App */}
            <button
              onClick={() => navigate("/fan")}
              className="w-full flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-md text-slate-200 hover:bg-slate-700 transition-colors"
            >
              <ArrowLeft size={18} />
              <span>Back to Fan App</span>
            </button>
            
            {/* User Profile Section */}
            <div className="flex items-center gap-3 px-3 py-2">
              <img
                src={currentUser?.profilePictureUrl}
                alt={currentUser?.name}
                className="w-10 h-10 rounded-full"
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">
                  {currentUser?.name}
                </p>
                <p className="text-xs text-slate-400">Creator Account</p>
              </div>
            </div>
          </div>
        }
      >
        <CreatorNavLinks textClassName="text-slate-200" />
      </MobileNavDrawer>
    </div>
  );
};

export default CreatorLayout;
